/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useRef, useState } from 'react'
import { Switch, Button } from '@intelie/ui'

import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'

import DeleteModal from './DeleteModal'
import { Template } from '../../../types'
import { goBack } from '../../../utils/functions'
import TemplateInfoModal from './TemplateInfoModal'
import { deleteTemplate } from '../../../services/template'
import { ANNOTATIONS_TEMPLATES_URL } from '../../../constants'
import { ColorSquare, EditorHeaderContainer, PopoverContainer } from '../../styles'

interface FormHeaderProps {
    isNew?: boolean
    template: Template
    setTemplate: React.Dispatch<React.SetStateAction<Template>>
}

export const EditorHeader: FC<FormHeaderProps> = ({ template, isNew, setTemplate }: FormHeaderProps) => {
    const [showPopover, setShowPopover] = useState<boolean>(false)
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
    const [showTemplateInfoModal, setShowTemplateInfoModal] = useState<boolean>(false)
    const [disableToggleTemplateState, setDisableToggleTemplateState] = useState<boolean>(false)

    const popoverRef = useRef()

    const onToggleActive = (): void => {
        setTemplate({ ...template, active: !template.active })
    }

    const togglePopover = (): void => {
        setShowPopover(!showPopover)
    }

    const handleDeleteTemplate = async (template: Template): Promise<void> => {
        try {
            await deleteTemplate(template.id)

            globalNotification.success(i18n('Template deleted successfully'), 5)

            goBack()
        } catch (error) {
            globalNotification.error(i18n(error.message), 5)
        }
    }

    const mustInactivateTemplate = (): void => {
        try {
            const schema = JSON.parse(template.schema) as FormSchema

            if (schema.elements.length <= 0) {
                setTemplate({ ...template, active: false })
                setDisableToggleTemplateState(true)

                return
            }
        } catch (error) {
            console.error(error)
        }

        setDisableToggleTemplateState(false)
    }

    useEffect(() => {
        if (isNew) {
            setShowTemplateInfoModal(true)
        }
    }, [])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setShowPopover(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        mustInactivateTemplate()
    }, [template.schema])

    return (
        <>
            <EditorHeaderContainer>
                <div className="left-header-content flex">
                    <a className="area-header-back" href={ANNOTATIONS_TEMPLATES_URL}>
                        <i className="icon-area-header-back" />
                    </a>
                    <h3 className="area-header-title text-overflow w-100">{template.alias || i18n('New template')}</h3>
                    <div className="template-popover">
                        <Button variant="text" onClick={togglePopover} className="template-info-button">
                            <i className="fas fa-wrench" />
                            <i className={`fas ${showPopover ? 'fa-caret-up' : 'fa-caret-down'}`}></i>
                        </Button>
                        {showPopover && (
                            <PopoverContainer ref={popoverRef}>
                                <Button
                                    variant="text"
                                    buttonFit
                                    onClick={() => setShowTemplateInfoModal(true)}
                                    icon="edit"
                                >
                                    {i18n('Edit template information')}
                                </Button>
                                <Button disabled variant="text" buttonFit onClick={() => {}} icon="copy">
                                    {i18n('Duplicate template')}
                                </Button>
                                <Button disabled variant="text" buttonFit onClick={() => {}} icon="trash-alt">
                                    {i18n('Remove')}
                                </Button>
                            </PopoverContainer>
                        )}
                    </div>
                </div>
                <div className="right-header-content">
                    <Switch
                        label={i18n('Enabled')}
                        active={template.active}
                        onChange={onToggleActive}
                        disabled={disableToggleTemplateState}
                    />
                    {template.perspective && (
                        <span>
                            <strong>{i18n('Perspective')}:</strong>
                            {template.perspective?.label}
                        </span>
                    )}
                    {template.color && (
                        <span>
                            <strong>{i18n('Color')}:</strong>
                            <ColorSquare color={template.color} title={template.color} />
                        </span>
                    )}
                </div>
            </EditorHeaderContainer>
            <TemplateInfoModal
                show={showTemplateInfoModal}
                setShow={setShowTemplateInfoModal}
                template={template}
                setTemplate={setTemplate}
            />
            <DeleteModal
                show={showDeleteModal}
                setShow={setShowDeleteModal}
                template={template}
                callbackDelete={handleDeleteTemplate}
            />
        </>
    )
}
